import { Text as ChakraText, type TextProps as ChakraTextProps } from '@chakra-ui/react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Tooltip, TooltipProps } from '.'

export interface Props extends ChakraTextProps {
  tooltipProps?: Omit<TooltipProps, 'children'>
}

export default forwardRef<HTMLParagraphElement, Props>(({ tooltipProps, ...rest }, ref) => {
  if (!rest.truncate && rest.lineClamp == null) return <ChakraText ref={ref} {...rest} />
  return <TextWithTooltip ref={ref} tooltipProps={tooltipProps} {...rest} />
})

const TextWithTooltip = forwardRef<HTMLParagraphElement, Props>(
  ({ children, tooltipProps, ...rest }, ref) => {
    const _ref = useRef<HTMLParagraphElement>(null)
    useImperativeHandle(ref, () => _ref.current!)

    const [truncate, setIsTruncated] = useState(false)

    useEffect(() => {
      const parent = _ref.current?.parentElement
      if (!parent) return

      const resizeObserver = new ResizeObserver(updateTruncated)
      resizeObserver.observe(parent)

      return () => resizeObserver.disconnect()
    }, [])

    useEffect(() => {
      updateTruncated()
    }, [children])

    function updateTruncated() {
      if (!_ref.current) return

      const { scrollWidth, clientWidth, scrollHeight, clientHeight } = _ref.current
      setIsTruncated(scrollWidth > clientWidth || scrollHeight > clientHeight)
    }

    return (
      <Tooltip disabled={!truncate} content={children} {...tooltipProps}>
        <ChakraText ref={_ref} {...rest}>
          {children}
        </ChakraText>
      </Tooltip>
    )
  }
)
